import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../api/baseApi.js";
import MasterDataService from "../../services/MasterDataService.js";
import { addToast } from "./toastSlice.js";
import getLanguage from "../../utils/getLanguage.js";
import { logoutUser } from "./authSlice.js";

const lang = getLanguage();

const postMethodOptions = (data, token) => {
  return {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: data,
    credentials: "include",
  };
};

// Async thunk for importing master data
export const importMasterData = createAsyncThunk(
  "masterData/import",
  async ({ file, dataType, options = {} }, { rejectWithValue, dispatch }) => {
    try {
      const result = await MasterDataService.importData(
        file,
        dataType,
        options,
        (progress) => {
          dispatch(updateImportProgress(progress));
        }
      );

      if (!result.success) {
        return rejectWithValue(result.error);
      }

      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk for importing customer data
 */
export const importCustomerData = createAsyncThunk(
  "customers/import",
  async ({ file, ...extraFields }, { getState, dispatch, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const form = new FormData();
      form.append("file", file);
      if (extraFields) {
        Object.entries(extraFields).forEach(([k, v]) =>
          form.append(k, String(v))
        );
      }

      const res = await fetch(
        `/api/customers/upload`,
        postMethodOptions(form, token)
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const message = err?.error || err?.message || "Upload failed";
        dispatch(
          addToast({
            message,
            source: "customer-upload",
            code: "ERR",
            type: "error",
            showViewLog: true,
          })
        );
        return rejectWithValue(err.errors || err);
      }

      const data = await res.json();

      if (data) {
        dispatch(
          addToast({
            message: "Customers uploaded successfully",
            source: "customers-upload",
            code: "200",
            type: "success",
          })
        );
        dispatch(api.util.invalidateTags([{ type: "Customers" }]));
      }
      return data;
    } catch (e) {
      const message = e.message || "Unexpected error";
      dispatch(
        addToast({
          message,
          source: "customer-upload",
          code: "ERR",
          type: "error",
        })
      );
      return rejectWithValue(e?.errors || e);
    }
  }
);

/**
 * Async thunk for importing product data with toasts
 */
export const importProductData = createAsyncThunk(
  "products/import",
  async ({ file, ...extraFields }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!file) {
        const message = "No file provided";
        dispatch(
          addToast({
            message,
            source: "products-upload",
            code: "400",
            type: "error",
          })
        );
        return rejectWithValue(message);
      }
      console.log("Importing products with extra fields:", extraFields);

      const token = getState().auth.token;

      const form = new FormData();
      form.append("file", file);
      if (extraFields) {
        Object.entries(extraFields).forEach(([k, v]) =>
          form.append(k, String(v))
        );
      }

      const res = await fetch(
        `/api/products/upload`,
        postMethodOptions(form, token)
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const message = err?.error || err?.message || "Upload failed";
        dispatch(
          addToast({
            message: message,
            source: "products-upload",
            code: String(res.status || "ERR"),
            type: "error",
            showViewLog: true,
          })
        );
        return rejectWithValue(err?.errors || err);
      }

      const data = await res.json().catch(() => ({}));
      dispatch(
        addToast({
          message: "Products uploaded successfully",
          source: "products-upload",
          code: "200",
          type: "success",
        })
      );
      // Invalidate products list
      dispatch(api.util.invalidateTags([{ type: "Products" }]));
      return data;
    } catch (err) {
      const message = err?.error || err?.message || "Unexpected error";
      dispatch(
        addToast({
          message,
          source: "products-upload",
          code: "ERR",
          type: "error",
        })
      );
      return rejectWithValue(message);
    }
  }
);
// Async thunk for validating import file
export const validateImportFile = createAsyncThunk(
  "masterData/validateFile",
  async ({ file, dataType }, { rejectWithValue }) => {
    try {
      const result = await MasterDataService.validateImportFile(file, dataType);
      return { ...result, dataType };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for exporting master data
export const exportMasterData = createAsyncThunk(
  "masterData/export",
  async (
    { dataType, filters, format, options },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await MasterDataService.exportData(
        dataType,
        filters,
        format,
        options,
        (progress) => {
          dispatch(updateExportProgress(progress));
        }
      );

      if (!result.success) {
        dispatch(
          addToast({
            message: result.error,
            source: "masterData/export",
            code: "ERR",
            type: "error",
          })
        );
        return rejectWithValue(result.error);
      }

      return result;
    } catch (error) {
      dispatch(
        addToast({
          message: error.message,
          source: "masterData/export",
          code: "ERR",
          type: "error",
        })
      );
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Import state
  isImporting: false,
  importProgress: {
    stage: "idle",
    progress: 0,
    message: "",
  },
  currentImport: null,

  // Validation state
  isValidating: false,
  validationResult: null,

  // Data preview
  previewData: null,

  // Import history
  importHistory: [],

  // Export state
  isExporting: false,
  exportProgress: {
    stage: "idle",
    progress: 0,
    message: "",
  },
  exportHistory: [],
  exportFilters: {
    dateFrom: null,
    dateTo: null,
    status: "all",
    search: "",
    format: "csv",
  },

  // Master data
  customers: [],
  products: [],
  references: [],
  distributors: [],

  // UI state
  selectedDataType: "customers",
  showPreview: false,
  showRollbackConfirm: false,
  rollbackImportId: null,

  // Error state
  error: null,
  validationErrors: [],
  importErrors: [],
};

const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {
    // UI actions
    setSelectedDataType: (state, action) => {
      state.selectedDataType = action.payload;
      state.validationResult = null;
      state.previewData = null;
      state.error = null;
    },

    setShowPreview: (state, action) => {
      state.showPreview = action.payload;
    },

    setShowRollbackConfirm: (state, action) => {
      state.showRollbackConfirm = action.payload.show;
      state.rollbackImportId = action.payload.importId || null;
    },

    // Progress updates
    updateImportProgress: (state, action) => {
      state.importProgress = { ...state.importProgress, ...action.payload };
    },

    updateExportProgress: (state, action) => {
      state.exportProgress = { ...state.exportProgress, ...action.payload };
    },

    // Clear actions
    clearValidationResult: (state) => {
      state.validationResult = null;
      state.previewData = null;
      state.validationErrors = [];
    },

    clearImportProgress: (state) => {
      state.importProgress = {
        stage: "idle",
        progress: 0,
        message: "",
      };
      state.currentImport = null;
    },

    clearError: (state) => {
      state.error = null;
      state.validationErrors = [];
      state.importErrors = [];
    },

    // Data management
    loadImportHistory: (state) => {
      state.importHistory = MasterDataService.getImportHistory();
    },
    // TODO: Delete it later
    loadMasterData: (state, action) => {
      const { dataType } = action.payload;
      const data =
        MasterDataService.loadFromStorage(`masterData_${dataType}`) || [];
      state[dataType] = data;
    },

    updateMasterData: (state, action) => {
      const { dataType, data } = action.payload;
      MasterDataService.saveMasterDataToStorage(dataType, data);
      state[dataType] = data;
    },

    createCustomer: (state, action) => {
      const newCustomer = action.payload;
      state.customers.push(newCustomer);
    },

    // Export actions
    setExportFilters: (state, action) => {
      state.exportFilters = { ...state.exportFilters, ...action.payload };
    },

    clearExportProgress: (state) => {
      state.exportProgress = {
        stage: "idle",
        progress: 0,
        message: "",
      };
    },

    loadExportHistory: (state) => {
      state.exportHistory = MasterDataService.getExportHistory();
    },

    // Reset state
    resetImportState: (state) => {
      state.isImporting = false;
      state.isValidating = false;
      state.validationResult = null;
      state.previewData = null;
      state.currentImport = null;
      state.showPreview = false;
      state.error = null;
      state.validationErrors = [];
      state.importErrors = [];
      state.importProgress = {
        stage: "idle",
        progress: 0,
        message: "",
      };
    },

    resetExportState: (state) => {
      state.isExporting = false;
      state.exportProgress = {
        stage: "idle",
        progress: 0,
        message: "",
      };
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(logoutUser.fulfilled, () => initialState)
      // Validate import file
      .addCase(validateImportFile.pending, (state) => {
        state.isValidating = true;
        state.validationResult = null;
        state.error = null;
        state.validationErrors = [];
        state.showPreview = false;
      })
      .addCase(validateImportFile.fulfilled, (state, action) => {
        state.isValidating = false;
        state.validationResult = action.payload;
        state.previewData = action.payload.preview;
        state.showPreview = true;

        if (!action.payload.isValid) {
          state.validationErrors = [action.payload.errors];
        } else {
          state.validationErrors = [];
        }
      })
      .addCase(validateImportFile.rejected, (state, action) => {
        state.isValidating = false;
        state.error = action.payload;
        state.validationErrors = [action.payload];
        state.showPreview = false;
      })

      // Import master data
      .addCase(importMasterData.pending, (state, action) => {
        state.isImporting = true;
        state.currentImport = {
          dataType: action.meta.arg.dataType,
          fileName: action.meta.arg.file.name,
          startTime: new Date().toISOString(),
        };
        state.error = null;
        state.importErrors = [];
      })
      .addCase(importMasterData.fulfilled, (state, action) => {
        state.isImporting = false;

        // Update import history
        state.importHistory.unshift(action.payload.importRecord);

        // Load updated data
        const dataType = state.currentImport.dataType;
        const data =
          MasterDataService.loadFromStorage(`masterData_${dataType}`) || [];
        state[dataType] = data;

        // Store any errors or warnings
        if (action.payload.errors.length > 0) {
          state.importErrors = action.payload.errors;
        }

        // Clear validation and preview
        state.validationResult = null;
        state.previewData = null;
        state.showPreview = false;
      })
      .addCase(importMasterData.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload;
        state.importErrors = [action.payload];
      })

      // Export master data
      .addCase(exportMasterData.pending, (state, action) => {
        state.isExporting = true;
        state.error = null;
      })
      .addCase(exportMasterData.fulfilled, (state, action) => {
        state.isExporting = false;

        // Update export history
        state.exportHistory.unshift(action.payload.exportRecord);
      })
      .addCase(exportMasterData.rejected, (state, action) => {
        state.isExporting = false;
        state.error = action.payload;
      })

      .addCase(importCustomerData.pending, (state, action) => {
        state.isImporting = true;
        state.currentImport = {
          dataType: action.meta.arg.dataType,
          fileName: action.meta.arg.file.name,
          startTime: new Date().toISOString(),
        };
        state.error = null;
        state.importErrors = [];
      })

      .addCase(importCustomerData.fulfilled, (state, action) => {
        state.isImporting = false;
        state.error = action.payload.error;
        state.validationResult = null;
        state.previewData = null;
        state.showPreview = false;
      })

      .addCase(importCustomerData.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload;
        Array.isArray(action.payload)
          ? (state.importErrors = action.payload)
          : (state.importErrors = [action.payload]);
      })

      .addCase(importProductData.pending, (state, action) => {
        state.isImporting = true;
        state.currentImport = {
          dataType: action.meta.arg.dataType,
          fileName: action.meta.arg.file.name,
          startTime: new Date().toISOString(),
        };
        state.error = null;
        state.importErrors = [];
      })

      .addCase(importProductData.fulfilled, (state, action) => {
        state.isImporting = false;
        state.error = action.payload.error;
        // Clear validation and preview
        state.validationResult = null;
        state.previewData = null;
        state.showPreview = false;
      })
      .addCase(importProductData.rejected, (state, action) => {
        state.isImporting = false;
        state.error = action.payload;
        Array.isArray(action.payload)
          ? (state.importErrors = action.payload)
          : (state.importErrors = [action.payload]);
      });
  },
});

export const {
  setSelectedDataType,
  setShowPreview,
  setShowRollbackConfirm,
  updateImportProgress,
  updateExportProgress,
  setExportFilters,
  clearValidationResult,
  clearImportProgress,
  clearExportProgress,
  clearError,
  loadImportHistory,
  loadExportHistory,
  loadMasterData,
  resetImportState,
  resetExportState,
  createCustomer,
  updateMasterData,
} = masterDataSlice.actions;

// Selectors
export const selectIsImporting = (state) => state.masterData.isImporting;
export const selectIsValidating = (state) => state.masterData.isValidating;
export const selectImportProgress = (state) => state.masterData.importProgress;
export const selectValidationResult = (state) =>
  state.masterData.validationResult;
export const selectPreviewData = (state) => state.masterData.previewData;
export const selectShowPreview = (state) => state.masterData.showPreview;
export const selectImportHistory = (state) => state.masterData.importHistory;
export const selectSelectedDataType = (state) =>
  state.masterData.selectedDataType;
export const selectMasterDataByType = (state, dataType) =>
  state.masterData[dataType] || [];
export const selectError = (state) => state.masterData.error;
export const selectValidationErrors = (state) =>
  state.masterData.validationErrors;
export const selectImportErrors = (state) => state.masterData.importErrors;
export const selectCurrentImport = (state) => state.masterData.currentImport;
export const selectShowRollbackConfirm = (state) =>
  state.masterData.showRollbackConfirm;
export const selectRollbackImportId = (state) =>
  state.masterData.rollbackImportId;

// Export selectors
export const selectIsExporting = (state) => state.masterData.isExporting;
export const selectExportProgress = (state) => state.masterData.exportProgress;
export const selectExportHistory = (state) => state.masterData.exportHistory;

export default masterDataSlice.reducer;
